import dayjs from 'dayjs';
import { DATE_FORMATS } from '@/utils/formUtils';

const keyOf = (v) => (v && typeof v === 'object' ? v.key : v);
const fmt = (value) =>
  value ? dayjs(value).format(DATE_FORMATS.DISPLAY) : '—';

export const TILE_LABELS = {
  osnovno: 'tileBasic',
  kontakt: 'tileContact',
  hr: 'tileHr',
  efakture: 'tileEInvoice',
  vreme: 'tileTime',
  moduli: 'tileModules',
  pristup: 'tileAccess',
  zaposleni: 'tileEmployees',
  ugovori: 'tileContracts',
  radnaMesta: 'tileJobPositions',
  oprema: 'tileEquipment',
  naknade: 'tileAllowances',
  obracun: 'tilePayroll',
  bonus: 'tileBonus',
  listici: 'tilePayslips',
  placanje: 'tilePayment',
  vlasnistvo: 'tileOwnership',
  upitnik: 'tileQuestionnaire',
  dokumenta: 'tileDocuments',
  sabloni: 'tileTemplates',
  istorija: 'tileHistory',
  tim: 'tileTeam',
  kontakti: 'tileClientContacts',
};

/**
 * Which tiles exist for this company and this user, grouped in display order.
 * Tabs of the old page keep their permission checks (`can`).
 */
export function buildCompanyTileGroups({
  company,
  can,
  showStats,
  showEquipment,
  showPayslips,
}) {
  const isBranch =
    keyOf(company?.applicationSettings?.typeOfCompany) === 'branch';

  const platformTiles = [
    'hr',
    // The old "Additional documents settings" drawer, next to
    // HR (fills the group's last row instead of adding one).
    company?.applicationSettings?.isHr && can.docsSettings && 'sabloni',
    'efakture',
    'vreme',
    'moduli',
    'pristup',
    can.team && 'tim',
  ].filter(Boolean);
  const payrollTiles = [
    !isBranch && 'naknade',
    'obracun',
    'bonus',
    // HLB publishes the AB payslips of a company that has any.
    showPayslips && 'listici',
    can.paymentInstructions && 'placanje',
  ].filter(Boolean);
  // The column never scrolls, so one more row takes the tiles' values away on
  // common screens. Payment instructions fill a half-empty platform row instead
  // of opening a row for the payslips tile.
  if (
    showPayslips &&
    can.paymentInstructions &&
    payrollTiles.length % 2 === 1 &&
    platformTiles.length % 2 === 1
  ) {
    payrollTiles.pop();
    platformTiles.push('placanje');
  }

  return [
    {
      id: 'data',
      label: 'groupData',
      tiles: ['osnovno', 'kontakt', can.clientContacts && 'kontakti'].filter(Boolean),
    },
    {
      id: 'platform',
      label: 'groupPlatform',
      tiles: platformTiles,
    },
    {
      id: 'employees',
      label: 'groupEmployees',
      tiles: [
        showStats && 'zaposleni',
        showStats && 'ugovori',
        // The company's job position catalog (HR companies).
        company?.applicationSettings?.isHr && 'radnaMesta',
        // Equipment held / awaited by the company's employees (HR companies,
        // people who may see it — decided by the overview's answer).
        company?.applicationSettings?.isHr && showEquipment && 'oprema',
      ].filter(Boolean),
    },
    {
      id: 'payroll',
      label: 'groupPayroll',
      tiles: payrollTiles,
    },
    {
      id: 'documents',
      label: 'groupDocuments',
      tiles: [
        'vlasnistvo',
        can.questionnaire && 'upitnik',
        (can.createDocuments || can.uploadDocuments) && 'dokumenta',
        'istorija',
      ].filter(Boolean),
    },
  ].filter((g) => g && g.tiles.length > 0);
}

/**
 * Short summary shown on a tile: { value, hint, warn, info }.
 * `tc` translates companyProfile keys, `te` employeeProfile keys.
 */
export function companyTileSummary(
  id,
  { company, stats, tc, te, t, team, jobPositions, equipment, clientContacts, payslipPublication }
) {
  const gd = company?.generalData || {};
  const app = company?.applicationSettings || {};
  const calc = company?.calculationSettings || {};
  const onOff = (v) => (v ? tc('on') : tc('off'));

  switch (id) {
    case 'kontakti':
      return {
        value: clientContacts ? String(clientContacts.contacts.length) : '—',
        hint: clientContacts ? t(`review.status.${clientContacts.review.status}`, { ns: 'teamContacts' }) : '',
        warn:
          clientContacts && clientContacts.review.status !== 'CONFIRMED'
            ? t('client.confirmTileHint', { ns: 'teamContacts' })
            : '',
      };
    case 'tim': {
      // People currently on the client and its status (team assignments).
      const people = new Set(
        (team?.assignments || []).map((a) => String(a.employee))
      ).size;
      const own = (team?.companies || []).find(
        (c) => String(c._id) === String(company?._id)
      );
      // Not classified yet counts as an active client, as on the board.
      const status = team ? own?.hlbClient?.status || 'ACTIVE' : null;
      return {
        value: team ? tc('teamPeople', { count: people }) : '—',
        hint: status ? t(`clientStatuses.${status}`, { ns: 'teamAssignments' }) : '',
      };
    }
    case 'radnaMesta': {
      const items = jobPositions?.items || [];
      const held = items.filter((p) => p.employeesCount > 0).length;
      return {
        value: jobPositions ? tc('jobPositionsCount', { count: items.length }) : '—',
        hint: items.length ? tc('jobPositionsHeld', { count: held }) : '',
      };
    }
    case 'oprema': {
      const counts = equipment?.counts;
      return {
        value: counts ? tc('equipmentAssigned', { count: counts.assigned }) : '—',
        hint: counts?.requested
          ? tc('equipmentWaiting', { count: counts.requested })
          : '',
        warn: counts?.leaving
          ? tc('equipmentLeaving', { count: counts.leaving })
          : '',
      };
    }
    case 'osnovno':
      return {
        value: [gd.code && `MB ${gd.code}`].filter(Boolean).join(' '),
        hint: [gd.pib && `PIB ${gd.pib}`, company?.market]
          .filter(Boolean)
          .join(' · '),
      };
    case 'kontakt':
      return {
        value: gd.city || te('notEntered'),
        hint: [gd.address, gd.email].filter(Boolean).join(' · '),
      };
    case 'hr':
      return {
        value: onOff(app.isHr),
        hint: app.holidayDaysTracker ? `${te('holidayTracker')} ✓` : '',
      };
    case 'efakture':
      return { value: onOff(app.eInvoice), hint: '' };
    case 'vreme':
      return {
        value: onOff(app.timeTracking),
        hint: app.timesheetReview?.enabled ? 'Timesheet ✓' : '',
      };
    case 'moduli': {
      const on = [
        app.toolsEnabled,
        app.calculatorEnabled,
        app.assistantEnabled,
        app.salaryTileEnabled !== false,
        app.activityFeedEnabled,
      ].filter(Boolean).length;
      return {
        value: tc('modulesOn', { count: on }),
        info: tc('infoModulesTile'),
      };
    }
    case 'pristup': {
      const ca = company?.clientsAdmins || {};
      const people =
        (ca.clients?.length || 0) +
        (ca.clientOfficers?.length || 0) +
        (ca.eInvoiceOfficers?.length || 0) +
        (ca.companyAdmins?.length || 0);
      return {
        value: tc('peopleCount', { count: people }),
        hint: [app.mfaEnabled && 'MFA', app.ssoOnly && 'SSO']
          .filter(Boolean)
          .join(' · '),
      };
    }
    case 'zaposleni':
      return {
        value: stats ? tc('activeCount', { count: stats.activeCount }) : '—',
        hint: stats
          ? tc('sectorsCount', {
              count: (stats.bySector || []).filter((s) => s.name).length,
            })
          : '',
        info: tc('infoEmployeesTile'),
      };
    case 'ugovori': {
      const expiring = (stats?.expiriesByMonth || []).reduce(
        (sum, m) => sum + m.count,
        0
      );
      return {
        value: stats ? tc('expiringCount', { count: expiring }) : '—',
        warn:
          stats?.expiredStillActive > 0
            ? tc('expiredActive', { count: stats.expiredStillActive })
            : '',
        info: tc('infoContractsTile'),
      };
    }
    case 'naknade':
      return {
        value:
          [
            keyOf(company?.allowances?.mealAllowance),
            keyOf(company?.allowances?.travelAllowance),
          ]
            .filter(Boolean)
            .map((k) => t(k, { ns: 'forms' }))
            .join(' · ') || '—',
        hint: '',
      };
    case 'obracun':
      return {
        value:
          keyOf(company?.salaryCalculationSettings?.salaryCalculationType) ===
          'fixed'
            ? tc('calcFixed')
            : tc('calcAverage'),
        hint: tc('workTypesCount', {
          count: (calc.workTypes || []).filter((w) => w?.dropdown).length,
        }),
      };
    case 'bonus':
      return {
        value:
          [
            calc.bonus && 'Bonus',
            calc.suspension && t('suspension', { ns: 'forms' }),
          ]
            .filter(Boolean)
            .join(' · ') || tc('off'),
        hint: '',
      };
    case 'listici': {
      const tz = (key, opts) => t(`compensation.${key}`, { ns: 'translations', ...opts });
      const label = (period) => `${period.slice(4)}.${period.slice(0, 4)}`;
      const through = payslipPublication?.publishedThrough;
      const newest = payslipPublication?.months?.[0];
      // What waits comes first: on a low screen only this line fits.
      return {
        value:
          newest && !newest.published
            ? tz('waitingPublication', { month: label(newest.period) })
            : tz('published'),
        hint: through
          ? tz('publishedThrough', { month: label(through) })
          : tz('nothingPublished'),
      };
    }
    case 'placanje':
      return {
        value: tc('slipsCount', {
          count: company?.companyPaymentsSlips?.length || 0,
        }),
        hint: '',
      };
    case 'vlasnistvo':
      return {
        value: tc('peopleCount', {
          count: company?.ownershipStructure?.length || 0,
        }),
        hint: '',
      };
    case 'upitnik': {
      const q = company?.questionnaireData;
      const filled = q && typeof q === 'object' && Object.keys(q).length > 0;
      return { value: filled ? te('entered') : te('notEntered'), hint: '' };
    }
    case 'dokumenta':
      return { value: tc('openList'), hint: '' };
    case 'sabloni': {
      const docs = company?.companyDocsSettings || {};
      const made = [
        'holidayLeave',
        'paidLeave',
        'nonPaidLeave',
        'bonus',
        'suspension',
        'maternityLeave',
        'pregnancyLeave',
        'additionalHolidayDays',
      ].filter((key) => docs[key] !== false).length;
      return {
        value: tc('templatesTypes', { count: made }),
        hint: docs.showMemorandumInDocs
          ? tc('templatesMemorandumOn')
          : tc('templatesMemorandumOff'),
      };
    }
    case 'istorija':
      return {
        value: fmt(company?.updatedAt),
        hint: `${te('createdAt')} ${fmt(company?.createdAt)}`,
      };
    default:
      return { value: '', hint: '' };
  }
}
